import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ImageHandler } from "../../hooks/ImageHandler";
import { getToken } from "../../services/tokenGET";
import useGET from "../../services/useGET";
import AdminSidebar from "../../components/layout/AdminSidebar";
import AddImageModal from "../../components/admin/species/AddImageModal";
import AddAudioModal from "../../components/admin/species/AddAudioModal";
import SpeciesForm from "../../components/admin/species/SpeciesForm";
import { useSpeciesData } from "../../hooks/admin/useSpeciesData";
import { useSpeciesImages } from "../../hooks/admin/useSpeciesImages";
import { useSpeciesAudio } from "../../hooks/admin/useSpeciesAudio";
import { useDropdownState } from "../../hooks/admin/useDropdownState";

export default function AdminSpeciesPage() {
    const { data: species, isPending } = useGET(`${process.env.REACT_APP_API_URL}/api/species`);
    const { data: areas } = useGET(`${process.env.REACT_APP_API_URL}/api/areas`);
    
    // Use custom hooks for state management
    const speciesData = useSpeciesData(species, areas);
    const imagesData = useSpeciesImages(speciesData.selectedItem);
    const audioData = useSpeciesAudio(speciesData.selectedItem);
    const categoryDropdown = useDropdownState();
    const habitatDropdown = useDropdownState();
    const zoneDropdown = useDropdownState();
    const speciesZoneDropdown = useDropdownState();
    
    // Remaining local state
    const [speciesType, setSpeciesType] = useState("");
    const [speciesName, setSpeciesName] = useState("");
    const [speciesHabitat, setSpeciesHabitat] = useState("");
    const [areasList, setAreasList] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredSpecies, setFilteredSpecies] = useState([]);
    const [selectedZones, setSelectedZones] = useState([]);
    const [categorySearchQuery, setCategorySearchQuery] = useState("");
    const [habitatSearchQuery, setHabitatSearchQuery] = useState("");
    const nameRef = useRef(null);
    const descriptionRef = useRef(null);
    const imageInputRef = useRef(null);

    const { handleFileChange, imageFileEncoded, setImageFileEncoded } = ImageHandler();

    // Fetches the categories from the database
    const { data, error } = useGET(`${process.env.REACT_APP_API_URL}/api/categories`);
    const categories = data ? data.map(item => item.name) : [];

    // Fetches the habitats from the database
    const { data: dataHabitat, errorHabitat } = useGET(`${process.env.REACT_APP_API_URL}/api/habitats`);
    const habitats = dataHabitat ? dataHabitat.map(test => test.description) : [];


    // Initialize filteredSpecies when speciesList changes
    useEffect(() => {
        setFilteredSpecies(speciesData.speciesList);
    }, [speciesData.speciesList]);

    useEffect(() => {
        if (areas) {
            setAreasList(areas);
        }
    }, [areas]);

    useEffect(() => {
        setImageFileEncoded(imageFileEncoded);
    }, [imageFileEncoded, setImageFileEncoded]);

    // Filter species based on search query and selected zones
    useEffect(() => {
        let filtered = speciesData.speciesList;

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(specie => 
                specie.name.toLowerCase().includes(query) ||
                specie.category_name.toLowerCase().includes(query)
            );
        }

        // Apply zone filter
        if (selectedZones.length > 0) {
            filtered = filtered.filter(specie => {
                const specieZones = speciesData.speciesAreaMap[specie.id] || [];
                return selectedZones.some(zoneId => specieZones.includes(zoneId));
            });
        }

        setFilteredSpecies(filtered);
    }, [searchQuery, speciesData.speciesList, selectedZones, speciesData.speciesAreaMap]);

    const handleSpeciesCategory = (event) => {
        setSpeciesType(event.target.value);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleZoneToggle = (zoneId) => {
        setSelectedZones(prev => {
            if (prev.includes(zoneId)) {
                return prev.filter(id => id !== zoneId);
            } else {
                return [...prev, zoneId];
            }
        });
    };

    const handleAdd = () => {
        setSpeciesType("");
        setSpeciesHabitat("")
        setCategorySearchQuery("");
        setHabitatSearchQuery("");
        categoryDropdown.setIsOpen(false);
        habitatDropdown.setIsOpen(false);
        speciesData.handleResetForm();
        imagesData.clearImages();
        audioData.clearSound();
        setImageFileEncoded("");

        if (imageInputRef.current) {
            imageInputRef.current.value = ""; 
        }
    };

    // Filter categories based on search query
    const filteredCategories = categories.filter(category => 
        category.toLowerCase().includes(categorySearchQuery.toLowerCase())
    );

    

    const handleCategorySelect = (category) => {
        setSpeciesType(category);
        setCategorySearchQuery(category);
        categoryDropdown.setIsOpen(false);
        speciesData.setNewSpecieData((prevState) => ({
            ...prevState,
            category_name: category,
        }));
    };

    // Filter Habitats based on search query
    const filteredHabitats = habitats.filter(habitat => 
        habitat.toLowerCase().includes(habitatSearchQuery.toLowerCase())
        
    );

    const handleHabitatSelect = (habitat) => {
        setSpeciesHabitat(habitat);
        setHabitatSearchQuery(habitat);
        habitatDropdown.setIsOpen(false);
        speciesData.setNewSpecieData((prevState) => ({
            ...prevState,
            habitat: habitat,
        }));
    };
    
    const handleClick = async (selected) => {
        speciesData.handleSelectSpecies(selected);
        setSpeciesName(selected.name);
        setSpeciesType(selected.category_name);
        setSpeciesHabitat(selected.habitat);
        setCategorySearchQuery(selected.category_name || "");
        setHabitatSearchQuery(selected.habitat || "");

        await imagesData.loadSpeciesImages(selected.id);
        await audioData.loadSpeciesSound(selected.id);
        console.log(audioData)
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-body">
            {/* Admin Sidebar */}
            <AdminSidebar />

            {/* Main Content - offset by sidebar width on desktop only */}
            <div className="flex-1 md:ml-64 flex flex-col">
                <main className="flex-grow">
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-forest to-leaf py-12">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="text-center">
                                <h1 className="font-heading text-4xl font-bold text-white sm:text-5xl">
                                    Redigera Artlista
                                </h1>
                                <p className="mt-4 text-lg text-white/90">
                                    Hantera arter och kategorier
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Content Grid */}
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left Panel - Species List */}
                            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                                <div className="p-6 border-b border-gray-200">
                                    <h2 className="font-heading text-2xl font-bold text-charcoal mb-4">
                                        Artlista
                                    </h2>
                                    
                                    {/* Search Bar */}
                                    <div className="relative mb-4">
                                        <input
                                            type="text"
                                            placeholder="Sök arter..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent"
                                        />
                                        <svg 
                                            className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>

                                    {/* Zone Filter Dropdown */}
                                    <div className="relative mb-4" ref={zoneDropdown.dropdownRef}>
                                        <button
                                            onClick={() => zoneDropdown.setIsOpen(!zoneDropdown.isOpen)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent text-left flex items-center justify-between bg-white hover:bg-gray-50"
                                        >
                                            <span className="text-gray-700">
                                                {selectedZones.length > 0 
                                                    ? `${selectedZones.length} område(n) vald` 
                                                    : 'Filtrera efter område'}
                                            </span>
                                            <svg 
                                                className={`w-5 h-5 text-gray-400 transition-transform ${zoneDropdown.isOpen ? 'rotate-180' : ''}`}
                                                fill="none" 
                                                stroke="currentColor" 
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        
                                        {zoneDropdown.isOpen && (
                                            <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                <div className="p-2">
                                                    {areasList && areasList.length > 0 ? (
                                                        areasList.map((area) => (
                                                            <label
                                                                key={area.id}
                                                                className="flex items-center px-3 py-2 hover:bg-gray-100 rounded cursor-pointer"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedZones.includes(area.id)}
                                                                    onChange={() => handleZoneToggle(area.id)}
                                                                    className="w-4 h-4 text-forest bg-white border-gray-300 rounded focus:ring-forest focus:ring-2"
                                                                />
                                                                <span className="ml-3 text-gray-700">{area.name}</span>
                                                            </label>
                                                        ))
                                                    ) : (
                                                        <p className="px-3 py-2 text-gray-500 text-sm">Inga områden tillgängliga</p>
                                                    )}
                                                </div>
                                                {selectedZones.length > 0 && (
                                                    <div className="border-t border-gray-200 p-2">
                                                        <button
                                                            onClick={() => setSelectedZones([])}
                                                            className="w-full px-3 py-2 text-sm text-forest hover:bg-sage/20 rounded transition-colors"
                                                        >
                                                            Rensa alla filter
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Add New Button */}
                                    <button
                                        onClick={handleAdd}
                                        className="w-full bg-forest hover:bg-leaf text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                                    >
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Lägg till ny art
                                    </button>
                                </div>
                                
                                <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                                    {filteredSpecies && filteredSpecies.length > 0 ? (
                                        <div className="p-4">
                                            {/* Group species by category */}
                                            {categories.map((category) => {
                                                const categorySpecies = filteredSpecies.filter(
                                                    (specie) => specie.category_name === category
                                                );
                                                
                                                if (categorySpecies.length === 0) return null;
                                                
                                                return (
                                                    <div key={category} className="mb-6">
                                                        <h3 className="font-semibold text-forest mb-2 px-2 text-sm uppercase tracking-wider">
                                                            {category} ({categorySpecies.length})
                                                        </h3>
                                                        <div className="space-y-2">
                                                            {categorySpecies.map((x) => (
                                                                <div
                                                                    key={x.id}
                                                                    onClick={() => {
                                                                        handleClick(x);
                                                                        // selectedItem is managed in the hook via handleSelectSpecies
                                                                    }}
                                                                    className={`p-4 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
                                                                        speciesData.selectedItem === x.id
                                                                            ? 'bg-sage border-forest shadow-md'
                                                                            : 'bg-white border-gray-200 hover:border-leaf hover:shadow-sm'
                                                                    }`}
                                                                >
                                                                    <div className="flex justify-between items-center">
                                                                        <div className="flex-1">
                                                                            <p className="font-semibold text-charcoal">{x.name}</p>
                                                                            {x.description && (
                                                                                <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                                                                    {x.description}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                        {speciesData.selectedItem === x.id && (
                                                                            <svg className="w-6 h-6 text-forest ml-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                            </svg>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="p-6 text-center text-gray-500">
                                            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <p className="font-semibold text-gray-700 mb-1">Inga arter hittades</p>
                                            <p className="text-sm text-gray-500">
                                                {searchQuery ? "Prova att söka efter något annat" : "Lägg till en ny art för att komma igång"}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Panel - Edit Species */}
                            <SpeciesForm
                                selectedItem={speciesData.selectedItem}
                                newSpecieData={speciesData.newSpecieData}
                                setNewSpecieData={speciesData.setNewSpecieData}
                                categorySearchQuery={categorySearchQuery}
                                setCategorySearchQuery={setCategorySearchQuery}
                                isCategoryDropdownOpen={categoryDropdown.isOpen}
                                setIsCategoryDropdownOpen={categoryDropdown.setIsOpen}
                                filteredCategories={filteredCategories}
                                speciesType={speciesType}
                                handleCategorySelect={handleCategorySelect}
                                setSpeciesType={setSpeciesType}
                                categoryDropdownRef={categoryDropdown.dropdownRef}
                                //TODO add habitat
                                habitatSearchQuery = {habitatSearchQuery}
                                setHabitatSearchQuery={setHabitatSearchQuery}
                                isHabitatDropdownOpen={habitatDropdown.isOpen}
                                setIsHabitatDropdownOpen={habitatDropdown.setIsOpen}
                                filteredHabitats={filteredHabitats}
                                speciesHabitat={speciesHabitat}
                                handleHabitatSelect={handleHabitatSelect}
                                setSpeciesHabitat={setSpeciesHabitat}
                                habitatDropdownRef={habitatDropdown.dropdownRef}


                                selectedSpeciesZones={speciesData.selectedSpeciesZones}
                                setSelectedSpeciesZones={speciesData.setSelectedSpeciesZones}
                                isSpeciesZoneDropdownOpen={speciesZoneDropdown.isOpen}
                                setIsSpeciesZoneDropdownOpen={speciesZoneDropdown.setIsOpen}
                                areasList={areasList}
                                handleSpeciesZoneToggle={speciesData.handleZoneToggle}
                                speciesZoneDropdownRef={speciesZoneDropdown.dropdownRef}
                                specieImageData={imagesData.specieImageData}
                                handleDeleteImage={imagesData.handleDeleteImage}
                                openAddImageModal={imagesData.openAddImageModal}
                                specieSound={audioData.specieSound}
                                openAddAudioModal={audioData.openAddAudioModal}
                                handleDeleteSound={audioData.handleDeleteSound}
                                handleSave={speciesData.handleSaveSpecies}
                                handleDeleteSpecies={speciesData.handleDeleteSpecies}
                            />
                        </div>
                    </div>
                </main>
            </div>

            {/* Modals */}
            <AddImageModal
                isOpen={imagesData.isAddImageModalOpen}
                onClose={imagesData.closeAddImageModal}
                specieImageData={imagesData.specieImageData}
                imageFiles={imagesData.imageFiles}
                setImageFiles={imagesData.setImageFiles}
                handleImageChange={imagesData.handleImageChange}
                handleAddImageInput={imagesData.handleAddImageInput}
                handleRemoveImageInput={imagesData.handleRemoveImageInput}
                handleDeleteImage={imagesData.handleDeleteImage}
                handleAddSpeciePictures={imagesData.handleAddSpeciePictures}
            />

            <AddAudioModal
                isOpen={audioData.isAddAudioModalOpen}
                onClose={audioData.closeAddAudioModal}
                specieSound={audioData.specieSound}
                audioFile={audioData.audioFile}
                handleAudioFileChange={audioData.handleAudioFileChange}
                handleAddSpecieSound={audioData.handleAddSpecieSound}
            />
        </div>
    );
}
